import React, { useRef, useState, useMemo, useCallback, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { IconType } from "react-icons";
import {
  OFF_WHITE,
  SIDEBAR_SELECTED_BG,
  SIDEBAR_HOVER_BG,
} from "../../constants/layout";
import SidebarFlyout from "./SidebarFlyout";
import { SidebarChild, SidebarSection } from "./sidebarNavConfig";

type SidebarIconItemProps = {
  label: string;
  icon: IconType;
  to?: string;
  children?: SidebarChild[];
  sections?: SidebarSection[];
  activePath?: string | null;
  onClick?: () => void;
};

const collectPaths = (
  children?: SidebarChild[],
  sections?: SidebarSection[]
): string[] => {
  const paths: string[] = [];
  children?.forEach((c) => paths.push(c.to));
  sections?.forEach((s) => s.items.forEach((c) => paths.push(c.to)));
  return paths;
};

const getSingleChild = (
  children?: SidebarChild[],
  sections?: SidebarSection[]
): SidebarChild | null => {
  const items = sections?.length
    ? sections.flatMap((section) => section.items)
    : children ?? [];

  if (items.length !== 1 || items[0].disabled) return null;
  return items[0];
};

const SidebarIconItem: React.FC<SidebarIconItemProps> = ({
  label,
  icon: Icon,
  to,
  children,
  sections,
  activePath,
  onClick,
}) => {
  const location = useLocation();
  const itemRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout>>();
  const [hovered, setHovered] = useState(false);
  const [flyoutTop, setFlyoutTop] = useState(0);

  const singleChild = useMemo(
    () => getSingleChild(children, sections),
    [children, sections]
  );

  const hasFlyout = Boolean(
    !singleChild && (children?.length || sections?.length)
  );

  const isActive = useMemo(() => {
    if (activePath === undefined) return false;
    const paths: string[] = [];
    if (to) paths.push(to);
    paths.push(...collectPaths(children, sections));
    return activePath !== null && paths.includes(activePath);
  }, [activePath, to, children, sections]);

  const openFlyout = useCallback(() => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    if (itemRef.current) {
      setFlyoutTop(itemRef.current.getBoundingClientRect().top);
    }
    setHovered(true);
  }, []);

  const closeFlyoutImmediately = useCallback(() => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setHovered(false);
  }, []);

  const closeFlyout = useCallback(() => {
    closeTimer.current = setTimeout(() => setHovered(false), 120);
  }, []);

  useEffect(
    () => () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
    },
    []
  );

  useEffect(() => {
    closeFlyoutImmediately();
  }, [location.pathname, closeFlyoutImmediately]);

  const isSelected = isActive || (hasFlyout && hovered);

  const itemSx = {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    gap: 0.5,
    py: 1.25,
    px: 0.5,
    mx: 0.75,
    borderRadius: "10px",
    cursor: "pointer",
    textDecoration: "none",
    color: OFF_WHITE,
    bgcolor: isSelected ? SIDEBAR_SELECTED_BG : "transparent",
    transition: "background-color 0.15s ease",
    "&:hover": {
      bgcolor: isSelected ? SIDEBAR_SELECTED_BG : SIDEBAR_HOVER_BG,
      color: OFF_WHITE,
    },
    "&[aria-current='page']": {
      color: OFF_WHITE,
      bgcolor: SIDEBAR_SELECTED_BG,
    },
    "& .MuiTypography-root": {
      color: `${OFF_WHITE} !important`,
    },
    "& svg": {
      color: OFF_WHITE,
      stroke: OFF_WHITE,
    },
  };

  const labelEl = (
    <Typography
      sx={{
        fontSize: 10,
        fontWeight: isSelected ? 600 : 500,
        lineHeight: 1.1,
        textAlign: "center",
        maxWidth: 64,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        color: `${OFF_WHITE} !important`,
      }}
    >
      {label}
    </Typography>
  );

  const iconEl = (
    <Icon
      size={20}
      strokeWidth={isSelected ? 2.25 : 2}
      color={OFF_WHITE}
      style={{ color: OFF_WHITE, stroke: OFF_WHITE }}
    />
  );

  if (singleChild) {
    return (
      <Box
        component={NavLink}
        to={singleChild.to}
        sx={itemSx}
        aria-label={label}
      >
        {iconEl}
        {labelEl}
      </Box>
    );
  }

  if (to && !hasFlyout) {
    return (
      <Box
        component={NavLink}
        to={to}
        sx={itemSx}
        aria-label={label}
      >
        {iconEl}
        {labelEl}
      </Box>
    );
  }

  if (onClick) {
    return (
      <Box
        ref={itemRef}
        onClick={onClick}
        sx={itemSx}
        role="button"
        tabIndex={0}
        aria-label={label}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") onClick();
        }}
      >
        {iconEl}
        {labelEl}
      </Box>
    );
  }

  return (
    <>
      <Box
        ref={itemRef}
        onMouseEnter={openFlyout}
        onMouseLeave={closeFlyout}
        sx={itemSx}
        aria-label={label}
        aria-haspopup="true"
      >
        {iconEl}
        {labelEl}
      </Box>

      {hasFlyout ? (
        <SidebarFlyout
          title={label}
          top={flyoutTop}
          visible={hovered}
          children={children}
          sections={sections}
          onMouseEnter={openFlyout}
          onMouseLeave={closeFlyout}
          onItemClick={closeFlyoutImmediately}
        />
      ) : null}
    </>
  );
};

export default SidebarIconItem;
